import React, { useState, useEffect, useCallback } from 'react';
import { leadsAPI } from '../../services/api';
import { getStatusColor, formatStatusCounts, calculateSuccessRate } from '../../utils/leadUtils';

function UserDashboard() {
  const userId = localStorage.getItem('id');
  const [recentLeads, setRecentLeads] = useState([]);
  const [userStats, setUserStats] = useState({ totalLeads: 0, statusCounts: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUserData = useCallback( async () => {
    setLoading(true);
    try {
      // Get user stats for accurate counts and recent leads for display
      const [statsResponse, recentLeadsResponse] = await Promise.all([
        leadsAPI.getUserStats(userId),
        leadsAPI.getAssignedLeads(userId, { limit: 5, sortBy: 'createdAt', sortOrder: 'desc' })
      ]);
      
      setUserStats(statsResponse.data || { totalLeads: 0, statusCounts: [] });
      setRecentLeads(recentLeadsResponse.data.leads || recentLeadsResponse.data || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  }, [userId]);


  useEffect(() => {
    if ( userId) {
          fetchUserData();
    }
  }, [userId, fetchUserData]);

  const statusStats = formatStatusCounts(userStats.statusCounts);



  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="bg-white p-6 rounded-2xl shadow-xl border border-gray-200/50">
        <h2 className="text-3xl font-bold mb-4">User Dashboard</h2>
        <p className="text-gray-600 mb-6">Welcome back! Here's an overview of your assigned leads.</p>
        
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-blue-600">Total Assigned</p>
                <p className="text-2xl font-bold text-blue-900">{userStats.totalLeads || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-6 rounded-lg border border-green-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 mr-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-green-600">Won Leads</p>
                <p className="text-2xl font-bold text-green-900">{statusStats['Closed Won'] || 0}</p>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-yellow-100 mr-4">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-yellow-600">Success Rate</p>
                <p className="text-2xl font-bold text-yellow-900">
                  {calculateSuccessRate(statusStats, userStats.totalLeads)}%
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Lead Status Distribution */}
          {/* <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">My Lead Status Distribution</h3>
            {userStats.statusCounts && userStats.statusCounts.length > 0 ? (
              <div className="space-y-3">
                {userStats.statusCounts.map((item) => (
                  <div key={item._id} className="flex items-center justify-between">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(item._id)}`}>
                      {item._id || 'Unknown'}
                    </span>
                    <span className="font-semibold">{item.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">
                {userStats.totalLeads === 0 ? 'No leads assigned yet' : 'Loading status distribution...'}
              </p>
            )}
          </div> */}

          {/* Recent Leads */}
          <div className="bg-gray-50 p-6 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Recent Leads</h3>
            {recentLeads.length > 0 ? (
              <div className="space-y-3">
                {recentLeads.map((lead) => (
                  <div key={lead._id} className="flex items-center justify-between p-3 bg-white rounded border">
                    <div>
                      <p className="font-medium text-gray-900">{lead.schoolName}</p>
                      <p className="text-sm text-gray-500">{lead.email}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(lead.progressStatus)}`}>
                      {lead.progressStatus}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No recent leads</p>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold mb-4 text-blue-900">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <a
              href="/user/lead-listing"
              className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition border border-blue-100"
            >
              <div className="p-2 bg-blue-100 rounded-lg mr-3">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">View All Leads</p>
                <p className="text-sm text-gray-500">Manage your assigned leads</p>
              </div>
            </a>
            
            <a
              href="/user/email-campaign"
              className="flex items-center p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition border border-blue-100"
            >
              <div className="p-2 bg-green-100 rounded-lg mr-3">
                <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Email Campaigns</p>
                <p className="text-sm text-gray-500">Send emails to leads</p>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserDashboard;